import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'react-feather';
import { Helmet } from 'react-helmet-async';
import JsonLd from './components/JsonLd';
import {
  SITE_URL,
  formatPageTitle,
  formatDescription,
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildWebPageSchema,
  buildSchemaGraph,
} from './utils/seo';
import { useDarkMode } from './DarkModeContext';
import { getCurrencySymbol } from './utils/currency';
import Footer from './Footer';

function About() {
    const { darkMode } = useDarkMode();
    const currency = getCurrencySymbol();
    const [openFAQ, setOpenFAQ] = useState(null);
    
    return (
        <>
        <Helmet>
            <title>{formatPageTitle('About Merra AI – AI Video Creation Platform')}</title>
            <meta name="description" content={formatDescription('Learn about Merra AI, the platform that turns small moments into marketing videos. Make video editing as easy as creating a tweet.')} />
            <meta name="keywords" content="about Merra AI, AI video creation, video editing platform, automated video editing, Merra AI company" />
            <link rel="canonical" href="https://www.merra.ai/about" />
            <meta property="og:title" content={formatPageTitle('About Merra AI – AI Video Creation Platform')} />
            <meta property="og:description" content={formatDescription('Learn about Merra AI, the platform that turns small moments into marketing videos.')} />
            <meta property="og:url" content="https://www.merra.ai/about" />
        </Helmet>
        <JsonLd
            schema={buildSchemaGraph([
                buildOrganizationSchema(),
                buildWebSiteSchema(),
                buildWebPageSchema({
                    name: formatPageTitle('About Merra AI – AI Video Creation Platform'),
                    description: 'Learn about Merra AI and our AI video creation platform.',
                    url: `${SITE_URL}/about`,
                }),
            ])}
        />
        <div style={{ 
            maxWidth: '800px', 
            margin: '0 auto', 
            padding: '20px',
            fontFamily: 'Arial, sans-serif',
            lineHeight: '1.6',
            color: darkMode ? '#e0e0e0' : '#333',
            backgroundColor: darkMode ? '#1a1a1a' : 'white',
            minHeight: '100vh'
        }}>
            <div style={{ marginBottom: '30px' }}>
                <Link to="/" style={{ color: darkMode ? '#4A9EFF' : '#1976d2', textDecoration: 'none' }}>
                    ← Back to Home
                </Link>
            </div>

            <h1 style={{ color: darkMode ? '#ffffff' : '#2c3e50', borderBottom: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`, paddingBottom: '10px' }}>
                About Merra AI
            </h1>

            {/* About Section */}
            <div style={{ marginTop: '30px', marginBottom: '50px' }}>
                <p style={{ fontSize: '18px', lineHeight: '1.8', marginBottom: '20px' }}>
                    On a mission to make video editing as easy as creating a tweet.
                </p>
                
                <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '20px' }}>
                    Merra AI is an AI powered video creation system that transforms small moments into high quality marketing videos.
                </p>
                
                <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '20px' }}>
                    Upload a handful of short clips and Merra AI turns them into dozens of voiceover style stories that feel natural, personal, and ready to post.
                </p>
                
                <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '20px' }}>
                    Most teams struggle with content because editing takes time, scripting feels heavy, and consistency is hard to maintain.
                </p>
                
                <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '20px' }}>
                    Merra removes that friction.
                </p>
                
                <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '20px' }}>
                    The system writes the script, finds the angle, trims the best shots, adds voiceover, captions, music, and delivers finished videos in minutes.
                </p>
                
                <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '20px' }}>
                    The goal is simple.
                </p>
                
                <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '20px' }}>
                    Make daily storytelling effortless for founders, creators, coaches, and small brands.
                </p>
                
                <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '20px' }}>
                    No complex timeline.
                </p>
                
                <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '20px' }}>
                    No editing skills.
                </p>
                
                <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '20px' }}>
                    Just real stories that grow your audience.
                </p>
                
                <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '20px' }}>
                    Merra is built for speed, simplicity, and expression.
                </p>
                
                <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '20px' }}>
                    If you want to create content consistently without burnout, Merra gives you the system to do it.
                </p>
            </div>

            {/* FAQ Section */}
            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '50px', marginBottom: '30px', borderBottom: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`, paddingBottom: '10px' }}>
                Frequently Asked Questions
            </h2>

            <div style={{ marginBottom: '30px' }}>
                {/* Landing Page FAQs */}
                <h3 style={{ color: darkMode ? '#e0e0e0' : '#34495e', marginTop: '30px', marginBottom: '20px' }}>General Questions</h3>
                
                {/* FAQ 1 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 1 ? null : 1)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>How does Merra.ai work?</span>
                        <span>{openFAQ === 1 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 1 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                Simply upload your raw video content, and our AI automatically edits it for you. 
                                No need for manual trimming, timeline editing, or technical skills. 
                                You can regenerate as many times as you want until you're satisfied with the result.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 2 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 2 ? null : 2)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>What types of videos can I create?</span>
                        <span>{openFAQ === 2 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 2 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                Merra.ai is perfect for short form social media content, and best for storytelling / voiceover content.
                                Our goal is to create a script that is engaging and interesting to watch, then we edit the video with filler content.
                                If you need content to exactly match the voiceover, for example a product demo, Merra won't be the best fit.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 3 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 3 ? null : 3)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>Do I need video editing experience?</span>
                        <span>{openFAQ === 3 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 3 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                Not at all! Merra.ai is designed for people who don't have time to edit videos 
                                or don't have video editing skills. Our AI does all the heavy lifting, you just 
                                upload and click 'regenerate' until you're happy.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 4 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 4 ? null : 4)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>What if I'm not satisfied with the results?</span>
                        <span>{openFAQ === 4 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 4 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                You can regenerate unlimited times at no extra cost. Our AI learns your preferences 
                                and each version gets better.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 5 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 5 ? null : 5)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>Is my video content secure?</span>
                        <span>{openFAQ === 5 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 5 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                Absolutely. We take data security seriously. Your content is encrypted, stored securely, 
                                and never shared with third parties. You can delete your content at any time.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 6 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 6 ? null : 6)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>Will it look AI-generated?</span>
                        <span>{openFAQ === 6 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 6 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                No! Merra.ai uses your own video content, so the final result looks completely authentic. 
                                Our AI simply edits and enhances YOUR video, it doesn't generate artificial content. 
                                You provide the footage, we provide the polish. The end result is professional-quality video 
                                that showcases your brand authentically.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 7 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 7 ? null : 7)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>Can I sign up for free?</span>
                        <span>{openFAQ === 7 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 7 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                Yes! Sign up is completely free with no credit card required. You can generate 2 videos, then you can upgrade to a paid plan. We offer flexible pricing plans 
                                based on your needs. Check out the pricing section for more details. You can cancel at any time. Start your free trial today and see the difference Merra.ai can make 
                                for your content creation.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 8 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 8 ? null : 8)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>How long does it take to create a video?</span>
                        <span>{openFAQ === 8 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 8 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                It usually takes 2 minutes to create a video.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 9 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 9 ? null : 9)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>Isn't it a bit expensive?</span>
                        <span>{openFAQ === 9 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 9 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                It's a lot, because we want you to post a lot of videos. Paying more gives you an extra incentive to post more videos. If you only paid {currency}10 a month, you would be fine posting 1 video and you wouldn't have any result.
                                <br/>
                                It's also much faster than any other cheaper tool (you save a ton of time if you usually edit your own videos, and this is valuable), and way cheaper than hiring a freelancer.
                            </p>
                        </div>
                    )}
                </div>

                {/* New FAQs */}
                <h3 style={{ color: darkMode ? '#e0e0e0' : '#34495e', marginTop: '40px', marginBottom: '20px' }}>Technical & Feature Questions</h3>

                {/* FAQ 10 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 10 ? null : 10)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>What happens if input clips have poor audio or lighting?</span>
                        <span>{openFAQ === 10 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 10 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                We don't have magic power (but with Merra, you'll feel like you do). Poor visual input = poor visual output, but we noticed that even with poor visuals, the story can hook viewers. So work on your content, but don't overthink it, start before you're ready, perfection is procrastination.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 11 */}
                {/* <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 11 ? null : 11)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>How many videos can it auto-generate from five clips monthly?</span>
                        <span>{openFAQ === 11 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 11 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                As many as you want, unlimited, but ideally, you should try do add more clips over time (that's what our top users do), otherwise is becomes repetitive, although, as long as the script create value and novelty, we noticed that it still deliver results, and most of your viewers won't see all of your content anyway so even if it's repetitive, it's fine.
                            </p>
                        </div>
                    )}
                </div> */}

                {/* FAQ 12 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 12 ? null : 12)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>Does it integrate with Drive, Dropbox, or social schedulers?</span>
                        <span>{openFAQ === 12 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 12 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                No, but that's on the roadmap.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 13 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 13 ? null : 13)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>How does it pick music and can I override?</span>
                        <span>{openFAQ === 13 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 13 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                It picks royalty free songs, to create a vibe without taking the attention away from the content. You can override, or even remove the music.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 14 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 14 ? null : 14)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>What control do I have over style and tone?</span>
                        <span>{openFAQ === 14 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 14 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                You can re-write everything, and regenerate everything. But no prompting, because most of the time, you don't know how to instruct the AI, so instead, let it create something different every time until it's close enough and you approve. But also we recommend to not waste too much time on that.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 15 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 15 ? null : 15)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>Can I upload existing brand assets and fonts?</span>
                        <span>{openFAQ === 15 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 15 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                Yes, although, if you want to add a logo and stuff, the best practice is to use another editing tool for that step, so you'll have control.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 16 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 16 ? null : 16)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>Does it create captions and are they accurate?</span>
                        <span>{openFAQ === 16 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 16 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                Yes. 100%. Next question.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 17 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 17 ? null : 17)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>What analytics or performance insights are provided?</span>
                        <span>{openFAQ === 17 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 17 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                Nothing now, you have analytics on the platforms that you post on, but coming soon here too.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 18 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 18 ? null : 18)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>Can it repurpose long videos into multiple shorts automatically?</span>
                        <span>{openFAQ === 18 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 18 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                No, use opusclips and submagic for that if you want. But if you really wish Merra did that, take 2 seconds and send a message to our cofounder <a href="https://www.linkedin.com/in/boukar-sall" target="_blank" rel="noopener noreferrer" style={{color: darkMode ? '#4A9EFF' : '#1976d2', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px'}}>Boukar <ExternalLink size={14} /></a> on LinkedIn.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 19 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 19 ? null : 19)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>What are average turnaround times per batch?</span>
                        <span>{openFAQ === 19 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 19 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                Batch production is coming soon, but on average, it takes 2 minutes per video.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 20 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 20 ? null : 20)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>How are my uploaded clips stored and secured?</span>
                        <span>{openFAQ === 20 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 20 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                Amazon AWS, so top secure.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 21 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 21 ? null : 21)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>Can I edit or reject specific auto-generated videos?</span>
                        <span>{openFAQ === 21 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 21 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                Yes, you can edit and regenerate everything.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 22 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 22 ? null : 22)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>What platforms and formats are exported by default?</span>
                        <span>{openFAQ === 22 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 22 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                Vertical only.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 23 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 23 ? null : 23)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>Are usage rights and licensing for music included?</span>
                        <span>{openFAQ === 23 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 23 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                Yes, next question.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 24 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 24 ? null : 24)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>How does it handle different languages and locales?</span>
                        <span>{openFAQ === 24 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 24 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                You can generate in any language, then translate in any language.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 25 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 25 ? null : 25)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>How does pricing scale with usage and seats?</span>
                        <span>{openFAQ === 25 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 25 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                8 videos per month for creator plan, and unlimited videos per month for pro plan, and unlimited seats in the pro plan.
                            </p>
                        </div>
                    )}
                </div>

                {/* FAQ 26 */}
                <div style={{marginBottom:'20px', border:`1px solid ${darkMode ? '#444' : '#ddd'}`, borderRadius:'8px', overflow:'hidden'}}>
                    <button 
                        onClick={() => setOpenFAQ(openFAQ === 26 ? null : 26)}
                        style={{
                            width:'100%',
                            padding:'20px',
                            backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                            border:'none',
                            textAlign:'left',
                            cursor:'pointer',
                            fontSize:'16px',
                            fontWeight:'bold',
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            color: darkMode ? '#ffffff' : '#2c3e50'
                        }}
                    >
                        <span>Are there voice options and can I use my voice?</span>
                        <span>{openFAQ === 26 ? '−' : '+'}</span>
                    </button>
                    {openFAQ === 26 && (
                        <div style={{padding:'20px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', borderTop:`1px solid ${darkMode ? '#444' : '#ddd'}`}}>
                            <p style={{margin:0, color: darkMode ? '#e0e0e0' : '#333'}}>
                                Yes, default voices and your own voice. But one voice per workspace, let us know if you need more, send a message to our cofounder <a href="https://www.linkedin.com/in/boukar-sall" target="_blank" rel="noopener noreferrer" style={{color: darkMode ? '#4A9EFF' : '#1976d2', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px'}}>Boukar <ExternalLink size={14} /></a> on LinkedIn.
                            </p>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </div>
        </>
    );
}

export default About;

